import React, { useEffect, useId, useRef, useState } from 'react';
import { CafClusterSample, CafEmbeddingItem, getEmailPreview, listSenderEmbeddings } from '../../../api/cafCorpus';
import { EmailBodyView } from '../EmailBody';
import { Attachments } from '../EmailSamples';
import { formatDayTime, formatWhen } from '../format';
import { senderEmail } from '../lenders';
import { SenderOption } from './senderOptions';

export const EmailPreviewPane: React.FC<{ preview: CafClusterSample | null; loading: boolean; footer?: React.ReactNode }> = ({
  preview, loading, footer,
}) => (
  <section className="cur-gbh-preview" aria-live="polite" aria-label="Email preview">
    {loading && <div className="cur-pv-empty">Loading the email…</div>}
    {!loading && !preview && <div className="cur-pv-empty">Click an email on the left, or press Show email, to read it here.</div>}
    {!loading && preview && (
      <>
        <div className="cur-pv-head">
          <div className="cur-pv-subj">{preview.subject || '(No subject)'}</div>
          <dl className="cur-pv-meta">
            <dt>From</dt><dd className="cur-ellip" title={senderEmail(preview.sender)}>{senderEmail(preview.sender)}</dd>
            <dt>Received</dt><dd className="cur-tnum">{formatWhen(preview.received_date)}</dd>
          </dl>
        </div>
        <div className="cur-pv-att">
          {preview.attachments_unavailable
            ? <span className="cur-muted">Attachments could not be loaded.</span>
            : preview.attachments && preview.attachments.length
            ? <Attachments items={preview.attachments} />
            : <span className="cur-muted">No attachments</span>}
        </div>
        <div className="cur-pv-body"><EmailBodyView body={preview.body ?? ''} title={`Email: ${preview.subject || 'no subject'}`} /></div>
        {footer && <div className="cur-pv-foot">{footer}</div>}
      </>
    )}
  </section>
);

interface Props {
  mode: 'many' | 'one';
  senders: SenderOption[];
  sender: string;
  onSenderChange: (value: string) => void;
  picked: string[];
  onPickedChange: (ids: string[]) => void;
  disabled?: boolean;
}

export const EmailPicker: React.FC<Props> = ({ mode, senders, sender, onSenderChange, picked, onPickedChange, disabled = false }) => {
  const id = useId();
  // Ryan's ruling (2026-09-29) overrides ticket #520's original "start unticked" default: the
  // box starts TICKED, matching the approved mockup. Unticking it searches the sender's full
  // kept history (ticket #520's behaviour survives as the opt-out, not the default).
  const [hide, setHide] = useState(true);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const [items, setItems] = useState<CafEmbeddingItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState('');
  const [active, setActive] = useState(-1);
  const [previews, setPreviews] = useState<Record<string, CafClusterSample>>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const list = useRef<HTMLUListElement | null>(null);
  // Final wave item 3 (ledger line 359): after the hide option changes, a pick the reloaded
  // list no longer shows is dropped, so it is neither counted nor submitted.
  const pruneOnLoad = useRef(false);
  const pickedNow = useRef({ picked, onPickedChange });
  pickedNow.current = { picked, onPickedChange };

  useEffect(() => {
    setItems([]);
    setCursor(null);
    setActive(-1);
    const prune = pruneOnLoad.current;
    pruneOnLoad.current = false;
    if (!sender) return undefined;
    let alive = true;
    setLoading(true);
    setProblem('');
    Promise.resolve(listSenderEmbeddings(sender, { limit: 50, excludeActioned: hide }))
      .then((page) => {
        if (!alive) return;
        setItems(page.items);
        setCursor(page.next_cursor);
        setActive(page.items.length ? 0 : -1);
        if (prune) {
          const shown = new Set(page.items.map((item) => item.id));
          const kept = pickedNow.current.picked.filter((itemId) => shown.has(itemId));
          if (kept.length !== pickedNow.current.picked.length) pickedNow.current.onPickedChange(kept);
        }
      })
      .catch(() => { if (alive) setProblem("Couldn't load this sender's emails. Try again."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sender, hide]);

  const activeId = active >= 0 ? items[active]?.id : undefined;
  useEffect(() => {
    if (!activeId || previews[activeId]) return undefined;
    let alive = true;
    setPreviewLoading(true);
    Promise.resolve(getEmailPreview(activeId))
      .then((preview) => { if (alive) setPreviews((prev) => ({ ...prev, [activeId]: preview })); })
      .catch(() => undefined)
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [activeId, previews]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const page = await listSenderEmbeddings(sender, { limit: 50, cursor, excludeActioned: hide });
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...page.items.filter((item) => !seen.has(item.id))];
      });
      setCursor(page.next_cursor);
    } catch {
      setProblem("Couldn't load more emails. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePick = (itemId: string) => {
    if (mode === 'one') onPickedChange([itemId]);
    else onPickedChange(picked.includes(itemId) ? picked.filter((value) => value !== itemId) : [...picked, itemId]);
  };

  const move = (next: number) => {
    const bounded = Math.max(0, Math.min(items.length - 1, next));
    setActive(bounded);
    list.current?.querySelector<HTMLButtonElement>(`[data-row="${bounded}"]`)?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    if (!items.length) return;
    move((active < 0 ? -1 : active) + (event.key === 'ArrowDown' ? 1 : -1));
  };

  const chooseSender = (value: string) => {
    onPickedChange([]);
    onSenderChange(value);
  };

  const listed = senders.some((option) => option.value === sender);
  const preview = activeId ? previews[activeId] ?? null : null;
  const inputLabel = mode === 'many' ? 'Add to group' : 'Use this email';

  return (
    <div className="cur-form">
      <div className="cur-field">
        <label htmlFor={`${id}-sender`}>Sender</label>
        <select id={`${id}-sender`} className="cur-input" value={listed ? sender : ''} disabled={disabled}
          onChange={(event) => chooseSender(event.target.value)}>
          <option value="">Choose a sender…</option>
          {senders.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {!typing ? (
          <div><button type="button" className="cur-linkish" onClick={() => setTyping(true)}>Sender not listed? Type the address</button></div>
        ) : (
          <div className="cur-btn-row">
            <input className="cur-input" style={{ maxWidth: 360 }} aria-label="Sender address" placeholder="name@lender.co.uk"
              value={typed} onChange={(event) => setTyped(event.target.value)} disabled={disabled} />
            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={disabled || !typed.includes('@')}
              onClick={() => chooseSender(typed.trim().toLowerCase())}>Find emails</button>
          </div>
        )}
      </div>
      <label className="cur-check">
        <input type="checkbox" checked={hide} disabled={disabled}
          onChange={(event) => { pruneOnLoad.current = true; setHide(event.target.checked); }} />
        <span>Hide emails that are already in a group or done
          <span className="cur-hint">Leave this off to search every email we have kept from this sender.</span>
        </span>
      </label>
      {problem && <div className="cur-alert cur-alert--error" role="alert">{problem}</div>}
      {sender && (
        <div>
          <div className="cur-label-row" style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>
              {mode === 'many' ? 'Read the emails, then tick the ones that belong together' : 'Read the emails, then choose one'}
            </span>
            {mode === 'many' && <span className="cur-muted cur-small">{picked.length} selected</span>}
          </div>
          <div className="cur-gbh-split">
            <div>
              <ul ref={list} className="cur-gbh-list" aria-label="Emails from this sender" onKeyDown={onKeyDown}>
                {items.map((item, index) => {
                  const on = index === active;
                  return (
                    <li key={item.id} className={`cur-gbh-row${on ? ' is-active' : ''}`} onClick={() => setActive(index)}>
                      <input type={mode === 'many' ? 'checkbox' : 'radio'} name={mode === 'one' ? `${id}-one` : undefined}
                        checked={picked.includes(item.id)} disabled={disabled}
                        aria-label={`${inputLabel}: ${item.subject || '(No subject)'}, received ${formatWhen(item.received_date)}`}
                        onClick={(event) => event.stopPropagation()} onChange={() => togglePick(item.id)} />
                      <div style={{ minWidth: 0 }}>
                        <div className="cur-gbh-row-top">
                          <span className="cur-ellip cur-gbh-subj" title={item.subject || ''}>{item.subject || '(No subject)'}</span>
                          <span className="cur-muted cur-small cur-tnum cur-nowrap">{formatDayTime(item.received_date)}</span>
                        </div>
                      </div>
                      <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm cur-gbh-show" aria-pressed={on} data-row={index}
                        onClick={(event) => { event.stopPropagation(); setActive(on ? -1 : index); }}>
                        {on ? 'Hide email' : 'Show email'}
                      </button>
                    </li>
                  );
                })}
                {!loading && !items.length && <li className="cur-empty">No emails found for this sender.</li>}
              </ul>
              {loading && <p className="cur-hint">Loading…</p>}
              {cursor && !loading && (
                <div className="cur-more-row">
                  <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => { void loadMore(); }}>Show more</button>
                </div>
              )}
              <p className="cur-hint" style={{ marginTop: 6 }}>
                Click a row to read it. {mode === 'many' ? 'Tick the box to add it to the group.' : 'Choose it with the round button.'} Use ↑ and ↓ to move between emails.
              </p>
            </div>
            <EmailPreviewPane preview={preview} loading={previewLoading && !preview}
              footer={activeId && (
                <label className="cur-check">
                  <input type={mode === 'many' ? 'checkbox' : 'radio'} checked={picked.includes(activeId)} disabled={disabled}
                    onChange={() => togglePick(activeId)} />
                  <span>{mode === 'many' ? 'Include this email in the group' : 'Use this email'}</span>
                </label>
              )} />
          </div>
        </div>
      )}
    </div>
  );
};
